// Blood pressure (systolic/diastolic) and SpO2 trend, the companion to
// TprChart — which already covers temperature, pulse and respiration.
//
// Same chart.js v3 registration caveat as TprChart: every controller/element/
// scale/plugin must be registered explicitly or the chart silently fails to
// render. Registering twice is harmless (chart.js dedupes), so this file
// registers its own rather than depending on TprChart having been imported.
import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Legend,
  Tooltip,
} from "chart.js";
import { Box, Typography } from "@mui/material";
import { istDayKey, formatIstTime } from "../../../utils/istDateTime";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, LineController, Legend, Tooltip);

// recorded_at is a UTC instant; slicing its raw digits labelled every point
// 5h30m early. istDayKey drops the year to keep the axis label short.
const fmtLabel = (dt) => {
  const day = istDayKey(dt);
  return day ? `${day.slice(5)} ${formatIstTime(dt)}` : "";
};

// blood_pressure is stored as free text ("138/86") — the model has no separate
// systolic/diastolic columns. Anything that isn't two numbers around a slash
// (an empty string, "unrecordable", a single number) charts as a gap rather
// than a guess.
function splitBp(text) {
  const match = /^\s*(\d{2,3})\s*\/\s*(\d{2,3})\s*$/.exec(String(text || ""));
  if (!match) return [null, null];
  return [Number(match[1]), Number(match[2])];
}

export default function BpSpo2Chart({ vitals = [] }) {
  const sorted = useMemo(
    () => [...vitals].sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at)),
    [vitals]
  );

  if (sorted.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
        No vitals recorded yet to chart.
      </Typography>
    );
  }

  const parsed = sorted.map((v) => splitBp(v.blood_pressure));

  const data = {
    labels: sorted.map((v) => fmtLabel(v.recorded_at)),
    datasets: [
      {
        label: "Systolic (mmHg)",
        data: parsed.map(([systolic]) => systolic),
        borderColor: "#d32f2f",
        backgroundColor: "#d32f2f",
        yAxisID: "mmhg",
        tension: 0.2,
      },
      {
        label: "Diastolic (mmHg)",
        data: parsed.map(([, diastolic]) => diastolic),
        borderColor: "#ef8a8a",
        backgroundColor: "#ef8a8a",
        yAxisID: "mmhg",
        tension: 0.2,
      },
      {
        label: "SpO₂ (%)",
        data: sorted.map((v) => v.oxygen_saturation ?? null),
        borderColor: "#2a78d6",
        backgroundColor: "#2a78d6",
        yAxisID: "pct",
        tension: 0.2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    scales: {
      mmhg: { type: "linear", position: "left", title: { display: true, text: "mmHg" } },
      pct: {
        type: "linear",
        position: "right",
        // Clinically interesting SpO2 lives in the top of the range; a 0-100
        // axis flattens the difference between 88% and 98% to nothing.
        min: 80,
        max: 100,
        title: { display: true, text: "SpO₂ %" },
        grid: { drawOnChartArea: false },
      },
    },
    plugins: {
      legend: { position: "bottom" },
    },
  };

  return (
    <Box sx={{ height: 260 }}>
      <Line data={data} options={options} />
    </Box>
  );
}
